import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import { breakpoints, Spacings } from '@styles/index';
import Paper from '@base/Paper/Paper';
import H2 from '@base/H2/H2';
import { useEffect } from 'react';
import usePracticeTopologies from '@api/hooks/practice/usePracticeTopologies';
import useTopology from '@api/hooks/topology/useTopology';
import useWindowSize from '@hooks/useWindowSize';
import GTPMobileBlocker from '@cont/GTP/GTPMobileBlocker';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import hasActiveSubscriptionToGoal from '@utils/subscriptionHelpers/hasActiveSubscriptionToGoal';
import pushRoute from '@utils/pushRoute';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import Topics from './TopicGroups';
import FreePracticeBanner from './FreePracticeBanner';

const Container = styled.div`
  grid-column: span 18;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    margin-bottom: 16px;
  }
`;

const Header = styled(Paper)`
  margin-bottom: ${Spacings.SPACING_10B};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_12B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    background-color: var(--color-base-0);
    box-shadow: none;
    padding: 24px 0 0;
  }
`;

const HeaderTitle = styled(H2)`
  margin-top: ${Spacings.SPACING_5B};
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-top: ${Spacings.SPACING_6B};
  }
`;

function PracticeTopics({ isGTP }) {
  const router = useRouter();
  const { goalUID, goalSlug } = router.query;

  const { topology: goalTopology } = useTopology(goalUID, {
    revalidateOnMount: false
  });

  const { myInfo } = useMyInfo();
  const isPlusUser = hasActiveSubscriptionToGoal(myInfo, goalUID);
  const { practiceTopologies } = usePracticeTopologies(goalUID);

  useEffect(() => {
    if (isPlusUser) {
      pushRoute(`/goal/${goalSlug}/${goalUID}/practice`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlusUser]);

  const { isMobile } = useWindowSize();

  const breadCrumb = [
    {
      title: goalTopology.name,
      href: '/goal/[goalSlug]/[goalUID]',
      as: goalTopology.relativeLink
    }
  ];

  if (isGTP && isMobile) {
    return <GTPMobileBlocker goalInfo={goalTopology} />;
  }
  const isNeetPG = goalUID === 'SDDOC';
  return (
    <Container>
      <Header gridColumn="span 18">
        <BreadCrumbs tags={breadCrumb} onBackClick={router.back} useBackClick />
        <HeaderTitle as="h1">
          {isNeetPG
            ? `${plusNavBarItems.Q_BANK} ${goalTopology.name}`
            : `Practice
          ${goalTopology.name}`}
        </HeaderTitle>
      </Header>
      <FreePracticeBanner goal={goalTopology} />
      <Topics
        isGTP={isGTP}
        topics={isGTP ? practiceTopologies.data : practiceTopologies}
      />
    </Container>
  );
}

export default PracticeTopics;
